import * as React from 'react'
import { FunctionComponent, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { DocumentContext, IRootOverviewItemDto } from '../../../config/documentConfig'
import { FilePartyAction, FilePartyDto, FilePartyRole, MainDto } from '../../../config/filePartyConfig'
import { IFileDto } from '../../../config/fileConfig'
import { FontWeights, getTheme, IconButton, ITheme, Link, mergeStyles, mergeStyleSets, Stack, StackItem, Text } from '@fluentui/react'
import IconValue from '../../IconValue/IconValue'
import { getPartyIcon } from '../../../helpers/iconHelper'
import { PartyType } from '../../Form/Party/PartyForm'
import BaseList, { baseListStyles } from '../../../components/List/BaseList'
import PartyConnectButton from '../../Form/PartyConnectButton'
import { useDispatch } from 'react-redux'
import useAxios from '../../../hooks/useAxios'
import { isAppContext } from '../../../helpers/appHelper'
import { AppContext } from '../../../config/appConfig'
import { useHistory } from 'react-router-dom'
import { isOfficeInitialized, outlookDisplayNewMessageForm } from '../../../helpers/outlookHelper'
import { formatSubject } from '../../Dialog/OutlookItem/ComposeOutlookItemDialog'
import DialogButton from '../../../components/Button/DialogButton'
import DeleteDialog, { DeleteDialogProps } from '../../../components/Dialog/LegacyDeleteDialog'
import GlobalContext, { FilePartyRoleDto, ModuleType } from '../../../context/GlobalContext'
import { getId } from '@fluentui/utilities'
import { useSettings } from '../../../hooks/useSettings'
import { AxiosInstance, AxiosResponse } from 'axios'
import { Destination } from '../../../context/AxiosContext'
import { ProofMeIcon } from '../../Icon/ProofMeIcon'
import useModules from '../../../hooks/module/useModules'
import { Tooltip } from '@fluentui/react-components'
import { AttachArrowRightRegular, CallRegular, MailRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import AutoSavingInput, { InputStatus } from '../../Input/AutoSavingInput'
import OfficeDocumentDialogBody, {
    getOfficeDocumentDialogBodySchema,
    OfficeDocumentDialogBodyProps,
    OfficeDocumentDialogBodyValues,
} from '../../../v2/components/Dialog/DriveItem/OfficeDocumentDialogBody'
import HammockFormDialog from '../../../v2/components/Dialog/HammockFormDialog'
import AuthorizationContext from '../../../context/AuthorizationContext'
import { composeAppointmentClick, composeEmailClick } from '../../../helpers/partyComposeHelper'

export type FilePartyListProps = {
    data: MainDto[]
    file: IFileDto
    documents: IRootOverviewItemDto
    reset?: () => any
}

type HeaderProps = {
    role: FilePartyRoleDto
    index: number
}

type FilePartyProps = FilePartyListProps & {
    party: FilePartyDto
}

enum ItemType {
    Header,
    Party,
}

type Item = { type: ItemType; item: FilePartyRoleDto | FilePartyDto }

const theme: ITheme = getTheme()
const styles = mergeStyleSets({
    headerCell: { borderTop: `1px solid ${theme.semanticColors.bodyDivider}` },
    list: {
        borderTop: `1px solid ${theme.semanticColors.menuDivider}`,
        borderBottom: `1px solid ${theme.semanticColors.menuDivider}`,
    },
    itemCell: [baseListStyles.itemCell, { border: 'none', ':last-child': {} }],
    role: { fontWeight: FontWeights.semibold },
    icon: { width: '32px', height: '32px', paddingLeft: '4px', paddingRight: '4px', borderBottomWidth: '0' },
})
const indentStyle = mergeStyles(styles.itemCell, { marginLeft: theme.spacing.l1 })

const getPartyUrl = (id: string, type: PartyType, prefix: string = ''): string => `${prefix}/party/${(type || '').toLowerCase()}/${id}/edit`

export async function createIdentificationRequest(axios: AxiosInstance, hostKey: string, party: FilePartyDto): Promise<void> {
    if (party.identificationRequestExternalId) {
        return Promise.resolve()
    }

    const response = await axios.post(`${process.env.API_HOST}/identification/proofme/identification/${hostKey}`, party.id, {
        baseURL: process.env.API_HOST,
        headers: { SuppressErrors: 'true', Destination: Destination.Hammock },
    })
    party.identificationRequestExternalId = response.data?.externalId
    party.identificationVerified = undefined
}

const HeaderCell: FunctionComponent<HeaderProps> = ({ role, index }: HeaderProps) => (
    <Stack styles={{ root: index !== 0 && styles.headerCell }} tokens={{ padding: 's1' }}>
        <Text variant="mediumPlus" className={styles.role}>
            {role.label}
        </Text>
    </Stack>
)

const FilePartyCell: FunctionComponent<FilePartyProps> = props => {
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const dispatch = useDispatch()
    const history = useHistory()
    const { settings } = useSettings()
    const modules = useModules()
    const roles: FilePartyRoleDto[] = settings?.filePartyRoles
    const { t: translate } = useTranslation()

    const [openCreateStandardDocument, setOpenCreateStandardDocument] = useState(false)

    const proofMeClick = () => {
        if (props.party.identificationVerified === true) {
            window.open(`https://dashboard.proofme.id/dashboard/identifications/${props.party.identificationRequestExternalId}`, '_blank')
        } else {
            createIdentificationRequest(axios, currentHost?.RowKey, props.party)
                .then(() => axios.get(`/party/${props.party.id}/identification/message`))
                .then(
                    (
                        message: AxiosResponse<{
                            body: string
                            subject: string
                        }>,
                    ) =>
                        outlookDisplayNewMessageForm({
                            toRecipients: (props.party.email || '').split(/[,;]/).map(email => ({
                                displayName: props.party.displayName,
                                emailAddress: email?.trim(),
                            })) as Office.EmailAddressDetails[],
                            subject: message.data.subject,
                            htmlBody: message.data.body?.replaceAll('<br/>', '\n'),
                        }),
                )
        }
    }

    return (
        <Stack styles={{ root: (props.party as MainDto).children ? styles.itemCell : indentStyle }} tokens={{ childrenGap: 's2' }}>
            <StackItem styles={{ root: baseListStyles.overflowCell }}>
                <IconValue
                    icon={getPartyIcon(props.party)}
                    value={
                        <Link onClick={() => history.push(getPartyUrl(props.party.id, props.party.type, `/file/${props.file.id}`))}>
                            {props.party.displayName}
                        </Link>
                    }
                />
            </StackItem>
            <Stack horizontal tokens={{ childrenGap: 's2 l1' }} wrap verticalAlign="center">
                <Stack horizontal tokens={{ childrenGap: 's2 l1' }} wrap verticalAlign="center">
                    {props.party.email && (
                        <StackItem>
                            <IconValue
                                icon={<MailRegular />}
                                value={
                                    <Link onClick={composeEmailClick(props.party, formatSubject(props.file, [props.party]))}>
                                        {props.party.email}
                                    </Link>
                                }
                                textVariant="small"
                            />
                        </StackItem>
                    )}
                    {props.party.phoneNumber && (
                        <StackItem>
                            <IconValue
                                value={<Link href={`tel:${props.party.phoneNumber}`}>{props.party.phoneNumber}</Link>}
                                icon={<CallRegular />}
                                textVariant="small"
                            />
                        </StackItem>
                    )}
                </Stack>
                {props.party?.type === PartyType.Legal && (
                    <StackItem grow>
                        <AutoSavingInput
                            contentBefore={
                                <div>
                                    <AttachArrowRightRegular />
                                    {props.party.reference && `${props.party.reference} - `}
                                </div>
                            }
                            appearance={'underline'}
                            required={props.party.fileReferenceRequired}
                            value={props.party.fileReference}
                            save={(value: string) =>
                                axios.put(`/file-party/${props.party.pivotId}`, { fileReference: value }).then(() => {
                                    props.party.fileReference = value
                                    return InputStatus.Success
                                })
                            }
                            placeholder={translate('party.reference.placeholder')}
                        />
                    </StackItem>
                )}
            </Stack>
            <Stack horizontal>
                <StackItem grow>
                    {props.party?.type === PartyType.Legal && (
                        <PartyConnectButton
                            menuPropsTitle={translate('file.pivot.party.add.contact')}
                            text={null}
                            fileId={props.file.id}
                            parent={props.party}
                            roles={roles}
                            translateFunction={translate}
                            reset={props.reset}
                        />
                    )}
                    <IconButton
                        as="a"
                        iconProps={{ iconName: 'OutlookLogo' }}
                        disabled={!props.party?.email}
                        onClick={composeEmailClick(props.party, formatSubject(props.file, [props.party]))}
                    />
                    {isOfficeInitialized() && !isAppContext([AppContext.Compose]) && (
                        <IconButton
                            as="a"
                            iconProps={{ iconName: 'Calendar' }}
                            disabled={!props.party?.email}
                            onClick={composeAppointmentClick(props.party, formatSubject(props.file, [props.party]))}
                        />
                    )}
                    <HammockFormDialog<Partial<OfficeDocumentDialogBodyValues>, OfficeDocumentDialogBodyProps>
                        open={openCreateStandardDocument}
                        setOpen={setOpenCreateStandardDocument}
                        dialogFormProps={() => ({
                            initialValues: {
                                fileId: props.file.id,
                                pivotId: props.party?.pivotId,
                            },
                            onSubmit: () => {},
                            validationSchema: getOfficeDocumentDialogBodySchema(translate),
                        })}
                        dialogFormBody={OfficeDocumentDialogBody}
                        dialogFormBodyProps={() => ({
                            context: DocumentContext.Party,
                            file: props.file,
                            documentRootId: props.file.documentRootId,
                            party: props.party,
                        })}
                    />
                    <IconButton as="a" iconProps={{ iconName: 'WordDocument' }} onClick={() => setOpenCreateStandardDocument(true)} />
                    {modules.has(ModuleType.ProofMe) && PartyType.Natural === props.party.type && (
                        <Tooltip
                            content={
                                props.party.identificationVerified === false
                                    ? translate('party.proofme.create')
                                    : props.party.identificationVerified === true
                                      ? translate('party.proofme.view')
                                      : translate('party.proofme.resend')
                            }
                            relationship={'label'}
                            withArrow
                        >
                            <Link onClick={proofMeClick} className={styles.icon}>
                                <ProofMeIcon filled={props.party.identificationVerified === true} />
                            </Link>
                        </Tooltip>
                    )}
                </StackItem>
                <DialogButton<DeleteDialogProps>
                    button={IconButton}
                    buttonProps={{ iconProps: { iconName: 'Delete' } }}
                    dialog={DeleteDialog}
                    dialogProps={{
                        dialogContentProps: {
                            title: translate('file.pivot.party.delete.title'),
                            subText: translate('file.pivot.party.delete.subtitle'),
                        },
                        onDelete: () =>
                            axios.delete(`/file-party/${props.party.pivotId}`).then(() => {
                                dispatch({ type: FilePartyAction.ResetOverview })
                                props.reset?.()
                            }),
                    }}
                    display="inline-block"
                />
            </Stack>
        </Stack>
    )
}

export default function FilePartyList(props: FilePartyListProps): ReactElement {
    const { settings } = useContext(GlobalContext)
    const [items, setItems] = useState<Item[]>()
    const roles: FilePartyRoleDto[] = settings?.filePartyRoles
    // Make sure that the list is re-rendered when we need to.
    const key = useMemo(getId, [props.file, props.documents, props.data])

    useEffect(() => {
        const match = (role: FilePartyRoleDto, party: MainDto) => party.role === role.type || (!party.role && role.type === FilePartyRole.Other)

        setItems(
            roles
                // The role contains more than 1 party.
                .filter((role: FilePartyRoleDto) => props.data.find((party: MainDto) => match(role, party)))
                .flatMap((role: FilePartyRoleDto) => [
                    { type: ItemType.Header, item: role },
                    ...props.data
                        .filter(party => match(role, party))
                        .sort((a: MainDto, b: MainDto) => a.displayName.localeCompare(b.displayName))
                        .flatMap(party => [party, ...party.children.map(child => ({ ...child, role: party.role }))])
                        .map((party: MainDto) => ({ type: ItemType.Party, item: party })),
                ]),
        )
    }, [props.data])

    return (
        <BaseList<Item> key={key} items={items} className={styles.list}>
            {(item: Item, index: number) => {
                switch (item.type) {
                    case ItemType.Header:
                        return <HeaderCell role={item.item as FilePartyRoleDto} index={index} />
                    case ItemType.Party:
                        return <FilePartyCell party={item.item as FilePartyDto} {...props} />
                }
            }}
        </BaseList>
    )
}
